import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { DialogService } from 'src/app/services/dialog.service';
import { formatError } from 'src/app/utils/errorMessage.formatter';
import { AgeTrack } from 'src/app/models/age-track/age-track.model';
import { AgeTrackService } from 'src/app/services/age-track.service';
import { CurriculumService } from 'src/app/services/curriculum.service';
import { HttpService } from 'src/app/services/http.service';
import { UnitService } from 'src/app/services/unit.service';

@Component({
  selector: 'app-sel-ages-editor',
  templateUrl: './sel-ages-editor.component.html',
  styleUrls: ['./sel-ages-editor.component.scss'],
  standalone: false,
})
export class SelAgesEditorComponent {
  age: AgeTrack = {
    order: 0,
    ageId: '',
    title: '',
    btnColor: '',
    gradientEnd: '',
    primaryColor: '',
    gradientStart: '',
    secondaryColor: '',
    accentColorTwo: '',
    accentColorOne: '',
    accentColorThree: '',
    titleColor: '',
  };
  saving: boolean;
  loading: boolean;
  deleting: boolean;
  emptyMandatoryFields = Array<string>();
  availableOrders: Array<{ id: string; label: string }> = [];
  unitsWithThisAgeId: Array<{
    age: string;
    ageUuid: string;
    ageOrder: number;
    hasChanges: boolean;
    units: Array<{ uuid: string; title: string; order: number; status: string }>;
  }> = [];
  hasUnsavedUnitChanges = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private httpService: HttpService,
    private ageService: AgeTrackService,
    private dialogService: DialogService,
    private curriculumService: CurriculumService,
    private unitService: UnitService
  ) {
    this.route.params.subscribe(data => {
      this.loading = true;
      if (data.ageId === 'new') {
        this.loadSelCurriculumData();
      } else {
        this.load(data.ageId);
      }
    });
  }

  loadAvailableOrders(ageId?: string) {
    this.availableOrders = [];

    this.ageService.list({ curriculum: environment.selCurriculumSlug }).subscribe(response => {
      for (var i = 0; i < response.ages.length; i++) {
        var order = (i + 1).toString();
        this.availableOrders.push({
          id: order,
          label: order,
        });
      }

      if (ageId === 'new' || !ageId || !this.age.order || response.ages.length <= 0) {
        var newOrder = (this.availableOrders.length + 1).toString();
        this.availableOrders.push({
          id: newOrder,
          label: newOrder,
        });
        this.age.order = this.availableOrders.length;
      }

      this.loading = false;
    });
  }

  loadSelCurriculumData() {
    this.curriculumService.get(environment.selCurriculumSlug).subscribe(response => {
      this.age.curriculumId = response.curriculum.curriculumId;
      this.loadAvailableOrders();
    });
  }

  load(ageId: string) {
    this.ageService.get(ageId).subscribe(
      response => {
        this.loading = false;
        this.age = response;
        this.loadAvailableOrders(ageId);
        this.loadUnitsWithThisAgeId(ageId);
      },
      errors => {
        this.loading = false;
        this.dialogService.showErrorAlert(formatError(errors?.error));
        this.goBack();
      }
    );
  }

  loadUnitsWithThisAgeId(ageId: string) {
    this.ageService.get(ageId).subscribe(
      response => {
        // Check if the response has associated units (API may return this separately)
        const associatedUnits = (response as any).associatedUnits;

        if (associatedUnits && associatedUnits.length > 0) {
          // Map the API response - for ages, units are not grouped by age
          // Create a single age group with the current age
          this.unitsWithThisAgeId = [
            {
              age: response.title || 'Units',
              ageUuid: response.ageId || ageId,
              ageOrder: response.order || 0,
              hasChanges: false,
              units: associatedUnits
                .map((unit: any) => ({
                  uuid: unit.uuid,
                  title: unit.title || '',
                  order: unit.order || 0,
                  status: unit.status || 'draft',
                }))
                .sort((a: any, b: any) => a.order - b.order),
            },
          ];
          this.hasUnsavedUnitChanges = false;
        }
      },
      error => {
        this.dialogService.showErrorAlert('Error loading age with units: ' + (error.message || 'Unknown error'));
      }
    );
  }

  trySave() {
    if (!this.allFieldsAreValid()) {
      return;
    }

    this.saving = true;

    // First, save any unit order changes
    this.saveUnitOrderChanges()
      .then(() => {
        // Then upload media files
        return Promise.all(this.getMediaReadyForUpload());
      })
      .then(() => {
        // Finally, save the age
        this.ageService.save(this.age).subscribe(
          response => {
            this.saving = false;
            this.dialogService.showSuccessAlert('Save Successful.');
            if (response) {
              this.router.navigate(['/sel-ages/' + response.ageId]);
            }
          },
          errors => {
            this.saving = false;
            this.dialogService.showErrorAlert(formatError(errors?.error));
          }
        );
      })
      .catch(error => {
        this.saving = false;
        this.dialogService.showErrorAlert('Error saving unit changes: ' + (error.message || 'Unknown error'));
      });
  }

  allFieldsAreValid(): boolean {
    this.emptyMandatoryFields = [];
    if (!this.age.title) {
      this.emptyMandatoryFields.push('title');
    }
    if (!this.age.scopePdfFile && !this.age.scopeFile) {
      this.emptyMandatoryFields.push('scopeFile');
    }
    if (!this.age.bannerFile && !this.age.banner) {
      this.emptyMandatoryFields.push('banner');
    }
    if (!this.age.imageFile && !this.age.image) {
      this.emptyMandatoryFields.push('image');
    }
    if (!this.age.gradeHeaderMascotFile && !this.age.mascotHeader) {
      this.emptyMandatoryFields.push('mascotHeader');
    }
    if (!this.age.titleColor) {
      this.emptyMandatoryFields.push('titleColor');
    }

    if (this.emptyMandatoryFields.length > 0) {
      setTimeout(() => {
        var errorMessage = document.querySelector('.error-message');
        errorMessage?.scrollIntoView({ behavior: 'smooth' });
      }, 1000);
    }

    return this.emptyMandatoryFields.length <= 0;
  }

  getMediaReadyForUpload(): Promise<string>[] {
    var promises: Promise<string>[] = [];
    if (this.age.scopePdfFile) {
      promises.push(
        this.createPromiseForMedia(this.age.scopePdfFile, 'file').then(url => {
          this.age.scopeFile = url;
          this.age.scopePdfFile = null;
          return url;
        })
      );
    }

    if (this.age.bannerFile) {
      promises.push(
        this.createPromiseForMedia(this.age.bannerFile, 'image').then(url => {
          this.age.banner = url;
          this.age.bannerFile = null;
          return url;
        })
      );
    }

    if (this.age.imageFile) {
      promises.push(
        this.createPromiseForMedia(this.age.imageFile, 'image').then(url => {
          this.age.image = url;
          this.age.imageFile = null;
          return url;
        })
      );
    }

    if (this.age.mascotFile) {
      promises.push(
        this.createPromiseForMedia(this.age.mascotFile, 'file').then(url => {
          this.age.mascot = url;
          this.age.mascotFile = null;
          return url;
        })
      );
    }

    if (this.age.gradeHeaderMascotFile) {
      promises.push(
        this.createPromiseForMedia(this.age.gradeHeaderMascotFile, 'file').then(url => {
          this.age.mascotHeader = url;
          this.age.gradeHeaderMascotFile = null;
          return url;
        })
      );
    }

    if (this.age.implementationGuideFile) {
      promises.push(
        this.createPromiseForMedia(this.age.implementationGuideFile, 'file').then(url => {
          this.age.implementationGuide = url;
          this.age.implementationGuideFile = null;
          return url;
        })
      );
    }

    return promises;
  }

  createPromiseForMedia(file: File, type: 'video' | 'image' | 'file'): Promise<string> {
    return new Promise((resolve, reject) => {
      this.httpService.uploadMedia(file, type).subscribe(
        r => resolve(r.secure_url),
        error => reject(error)
      );
    });
  }

  goBack() {
    this.router.navigate(['/sel-ages']);
  }

  onUnitsChanged(updatedUnits: any[]) {
    this.unitsWithThisAgeId = updatedUnits;
  }

  saveUnitOrderChanges(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Find all age groups with changes
      const ageGroupsWithChanges = this.unitsWithThisAgeId.filter(ag => ag.hasChanges);

      if (ageGroupsWithChanges.length === 0) {
        resolve();
        return;
      }

      // Collect all units that need to be updated
      const unitUpdatePromises: Promise<any>[] = [];

      ageGroupsWithChanges.forEach(ageGroup => {
        ageGroup.units.forEach((unit, index) => {
          const newOrder = index + 1; // 1-based index as the new order
          // Create a promise for each unit update
          const updatePromise = new Promise((resolveUnit, rejectUnit) => {
            this.unitService.get(unit.uuid).subscribe(
              fullUnit => {
                // Update the order on the full unit object
                fullUnit.unit.order = newOrder;

                // Save the updated unit
                this.unitService.save(fullUnit.unit).subscribe(
                  response => {
                    resolveUnit(response);
                  },
                  error => {
                    rejectUnit(new Error(`Failed to save unit ${unit.title}: ${error.message || 'Unknown error'}`));
                  }
                );
              },
              error => {
                rejectUnit(new Error(`Failed to fetch unit ${unit.title}: ${error.message || 'Unknown error'}`));
              }
            );
          });

          unitUpdatePromises.push(updatePromise);
        });
      });

      // Wait for all unit updates to complete
      Promise.all(unitUpdatePromises)
        .then(() => {
          // Reset the hasChanges flags since changes are now saved
          this.unitsWithThisAgeId.forEach(ag => (ag.hasChanges = false));
          this.hasUnsavedUnitChanges = false;
          resolve();
        })
        .catch(error => {
          reject(error);
        });
    });
  }
}
